import { graphql, Link } from 'gatsby';

import { Button, styled } from '@mui/material';

import {
  ConversionTeaser,
  ConversionTeaserBase,
  ConversionTeaserLight,
} from '@mewa/ui';

import { markdownToHTML } from '../../../../libs/markdown-to-html';
import { imagePopulate } from '../../../../libs/strapi-populates';
import { ComponentProps, PossibleContentSections } from '../fragment-and-types';
import {
  ContentLivePreviewConfig,
  ImageStrapiRest,
} from '../live-preview/types';
import { StrapiImage, StrapiResponsiveImageData } from './image';

export const ConversionTeaserFragment = graphql`
  fragment ConversionTeaserFragment on STRAPI__COMPONENT_SHARED_CONVERSION_TEASER {
    headline: Headline
    labelCTA: LabelCTA
    linkUrlCTA: LinkUrlCTA
    piwikEventNameCTA: PiwikEventNameCTA
    showCTA
    content: Description {
      data {
        childMarkdownRemark {
          html
        }
      }
    }
    image: Image {
      ... on STRAPI__COMPONENT_SHARED_RESPONSIVE_IMAGE {
        id
        strapi_component
        imageAlternativeText
        imageDesktop {
          ...StrapiImageFileFragment
        }
        imageMobile {
          ...StrapiImageFileFragment
        }
      }
    }
    variant: Variant
    strapi_component
  }
`;

export const componentName = 'shared.conversion-teaser';

export type ConversionTeaserData = {
  headline: string;
  content: {
    data: {
      childMarkdownRemark: { html: string };
    };
  };
  showCTA?: boolean;
  labelCTA: string;
  linkUrlCTA: string;
  piwikEventNameCTA: string;
  image: StrapiResponsiveImageData;
  variant: 'LightBackground' | 'ImageBackground';
  strapi_component: typeof componentName;
};

export type ConversionTeaserPreview = {
  strapi_component: typeof componentName;
  Headline: string;
  Description: string;
  Variant: 'ImageBackground' | 'LightBackground';
  LabelCTA: string;
  LinkUrlCTA: string;
  PiwikEventNameCTA: string;
  Image: ImageStrapiRest;
};

export const isConversionTeaser = (
  component: PossibleContentSections,
): component is ConversionTeaserData => {
  return component.strapi_component === 'shared.conversion-teaser';
};

export const StrapiConversionTeaser = ({
  content,
  headline,
  labelCTA,
  showCTA,
  image,
  variant,
  linkUrlCTA,
  conversion,
  piwikEventNameCTA,
  sapForm,
}: ComponentProps<ConversionTeaserData>) => {
  const props: ConversionTeaserBase<typeof Link> = {
    content: content.data.childMarkdownRemark.html,
    headline: headline,
    labelCTA: labelCTA,
    showCTA: showCTA,
    link: {
      component: Link,
      props: {
        to: linkUrlCTA || conversion.link,
        target: linkUrlCTA ? '_blank' : undefined,
      },
    },
    piwikEventNameCTA: piwikEventNameCTA,
    image: <StrapiImage responsiveImage={image} alt={headline} />,
  };

  const sapFormButton = sapForm && (
    <Button
      onClick={() => {
        document.getElementById('sapForm')?.scrollIntoView({
          behavior: 'smooth',
        });
      }}
      variant="outlined"
      color={variant !== 'LightBackground' ? 'inverted' : 'primary'}
      sx={{
        color: variant !== 'LightBackground' ? 'var(--color-white)' : 'auto',
      }}
    >
      {sapForm.scrollToFormButtonLabel}
    </Button>
  );

  return (
    <Wrapper>
      {variant === 'LightBackground' ? (
        <ConversionTeaserLight {...props} customButton={sapFormButton} />
      ) : (
        <ConversionTeaser
          {...props}
          sx={{ marginTop: 0 }}
          customButton={sapFormButton}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled('div')(({ theme }) => ({
  marginTop: '50px',
  marginBottom: '100px',

  [theme.breakpoints.up('md')]: {
    marginBottom: '140px',
  },
}));

export const config = {
  isComponent: isConversionTeaser,
  Component: StrapiConversionTeaser,
  componentName,
};

export const previewConfig: ContentLivePreviewConfig = {
  populationParams: { Image: imagePopulate },
  componentName,
  mapProperties: (conversionTeaser): ConversionTeaserData | null =>
    conversionTeaser.strapi_component === componentName
      ? {
          ...conversionTeaser,
          content: markdownToHTML(conversionTeaser.Description),
          headline: conversionTeaser.Headline,
          image: conversionTeaser.Image,
          labelCTA: conversionTeaser.LabelCTA,
          linkUrlCTA: conversionTeaser.LinkUrlCTA,
          piwikEventNameCTA: conversionTeaser.PiwikEventNameCTA,
          variant: conversionTeaser.Variant,
        }
      : null,
};
